import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  Armchair,
  User,
  ShieldCheck,
} from "lucide-react";
import {
  getBoletoById,
  getComprasBySocio,
  hidratarComprasBoletos,
} from "@/data/comprasStore";
import { getCurrentSocio } from "@/data/socioAuthStore";
import { BOLETO } from "@/constants/testIds";
import { formatearFechaLarga } from "@/utils/fechas";

export default function BoletoDigital() {
  const { boletoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const socio = useMemo(() => getCurrentSocio(), []);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    hidratarComprasBoletos().then(() => setTick((t) => t + 1));
  }, []);

  const boleto = useMemo(
    () => (socio ? getBoletoById(boletoId, socio.id) : null),
    [boletoId, socio, tick],
  );

  const compra = useMemo(() => {
    if (!boleto || !socio) return null;
    return (
      getComprasBySocio(socio.id).find((c) => c.id === boleto.compraId) || null
    );
  }, [boleto, socio, tick]);

  if (!socio) return <Navigate to="/login" replace />;
  if (!boleto || !compra) return <Navigate to="/app/mis-boletos" replace />;

  const validado = boleto.estado === "VALIDADO";
  const listo = boleto.estado === "LISTO" && boleto.qrEstado === "VIGENTE";

  return (
    <div data-testid={BOLETO.screen} className="flex flex-col gap-6 pb-8">
      <button
        type="button"
        data-testid={BOLETO.btnRegresar}
        onClick={() =>
          navigate(location.state?.from || `/app/asignar/${boleto.compraId}`)
        }
        aria-label="Regresar"
        className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
      >
        <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        </span>
        Regresar
      </button>

      <header className="animate-fade-up">
        <span className="overline text-accent">Boleto</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
          {compra.nombreEvento}
        </h1>
      </header>

      <section className="rounded-2xl border hairline bg-card p-6 md:p-8 shadow-soft animate-fade-up delay-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Info */}
          <ul className="flex flex-col gap-3 text-sm">
            <li className="flex items-center gap-3">
              <CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div>
                <div className="overline text-muted-foreground">Fecha</div>
                <div data-testid={BOLETO.fecha} className="text-foreground">
                  {formatearFechaLarga(compra.fechaEvento)}
                </div>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div>
                <div className="overline text-muted-foreground">Hora</div>
                <div data-testid={BOLETO.hora} className="text-foreground">
                  {compra.horaEvento}
                </div>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div>
                <div className="overline text-muted-foreground">Salón</div>
                <div data-testid={BOLETO.salon} className="text-foreground">
                  {boleto.salon || compra.salon}
                </div>
              </div>
            </li>
            {boleto.mesa !== null && boleto.mesa !== undefined && (
              <li className="flex items-center gap-3">
                <Armchair className="w-4 h-4 text-accent" strokeWidth={1.6} />
                <div>
                  <div className="overline text-muted-foreground">Mesa</div>
                  <div data-testid={BOLETO.mesa} className="text-foreground">
                    Mesa {boleto.mesa}
                  </div>
                </div>
              </li>
            )}
            <li className="flex items-center gap-3">
              <User className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div>
                <div className="overline text-muted-foreground">Asistente</div>
                <div data-testid={BOLETO.asistente} className="text-foreground">
                  {boleto.asistenteNombre}
                </div>
              </div>
            </li>
          </ul>

          {/* QR */}
          <div className="flex flex-col items-center justify-center gap-3">
            <div
              data-testid={BOLETO.qr}
              className="p-4 rounded-2xl bg-white ring-1 ring-border shadow-soft"
            >
              {listo && boleto.qrToken ? (
                <QRCodeSVG
                  value={boleto.qrToken}
                  size={196}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={false}
                />
              ) : (
                <div className="w-[196px] h-[196px] grid place-items-center text-xs text-muted-foreground text-center px-6 bg-muted rounded-xl">
                  QR no disponible
                </div>
              )}
            </div>
            {validado && (
              <div
                data-testid={BOLETO.msgValidado}
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              >
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                Este boleto ya fue utilizado.
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Presenta este código al ingresar al evento.
        </p>
      </section>
    </div>
  );
}
